/**
 * Error Boundary — captura erros por rota (spec §11.9)
 *
 * React 19 com novo hook useRouteError para React Router.
 * Cada rota tem seu próprio boundary para isolamento de falhas.
 */

import React from 'react'
import { useRouteError, isRouteErrorResponse, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'

// ─── Error Boundary de rota (React Router) ────────────────────────────────────

export function RouteErrorBoundary() {
  const error = useRouteError()

  let title = 'Algo deu errado'
  let message = 'Ocorreu um erro inesperado. Tente novamente.'
  let statusCode: number | undefined

  if (isRouteErrorResponse(error)) {
    statusCode = error.status
    if (error.status === 404) {
      title = 'Página não encontrada'
      message = 'A página que você procura não existe ou foi movida.'
    } else if (error.status === 403) {
      title = 'Sem permissão'
      message = 'Você não tem acesso a esta funcionalidade.'
    }
  }

  return (
    <main
      className="flex min-h-screen flex-col items-center justify-center gap-6 p-8 text-center"
      role="alert"
      aria-live="assertive"
    >
      {statusCode && (
        <span className="text-6xl font-bold text-gray-200 dark:text-gray-800">
          {statusCode}
        </span>
      )}
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-50">
        {title}
      </h1>
      <p className="max-w-md text-gray-500 dark:text-gray-400">{message}</p>
      <div className="flex flex-wrap justify-center gap-3">
        <Button variant="outline" onClick={() => window.location.reload()}>
          Tentar novamente
        </Button>
        <Button asChild>
          <Link to="/">Voltar ao início</Link>
        </Button>
        <Button variant="ghost" onClick={() => { void encerrarSessao() }}>
          Sair
        </Button>
      </div>
    </main>
  )
}

// Saída de emergência do boundary de última instância: erro determinístico
// (sessão corrompida, perfil inválido) transforma "Tentar novamente" em loop de
// reload. Não usa useAuth de propósito — o contexto de auth pode ser justamente
// o que quebrou.
async function encerrarSessao(): Promise<void> {
  const base = import.meta.env.VITE_API_URL ?? ''
  try {
    await fetch(`${base}/bff/auth/logout`, { method: 'POST', credentials: 'include' })
  } catch {
    // O cookie httpOnly expira sozinho; o access token vive só em memória e
    // morre no reload abaixo.
  }
  window.location.assign('/login')
}

// ─── Error Boundary de componente (classe) ────────────────────────────────────

interface ComponentErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

interface ComponentErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

export class ComponentErrorBoundary extends React.Component<
  ComponentErrorBoundaryProps,
  ComponentErrorBoundaryState
> {
  constructor(props: ComponentErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ComponentErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Em produção: enviar para Sentry/GlitchTip com scrub de PII
    console.error('[ComponentErrorBoundary]', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div
          role="alert"
          className="flex flex-col items-center gap-4 rounded-lg border border-danger-200 bg-danger-50 p-8 text-center"
        >
          <p className="font-medium text-danger-700">
            Este componente encontrou um erro.
          </p>
          <p className="max-w-md text-sm text-danger-700/80">
            O restante da navegação continua funcionando — use o menu para abrir
            outra tela.
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              Tentar novamente
            </Button>
            <Button size="sm" asChild>
              <Link to="/">Voltar ao início</Link>
            </Button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
